'use client';

import React, { useState } from 'react';
import { VS_CODE_EXTENSION_LINK, CURSOR_EXTENSION_LINK } from '@/utils/links';
import { saveEmailToSupabase } from '@/utils/supabase';

type EmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  source: 'vscode' | 'cursor';
};

export default function EmailModal({
  isOpen,
  onClose,
  source,
}: EmailModalProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic email validation
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await saveEmailToSupabase(email, source);

      if (result.success) {
        setSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadNow = () => {
    const link =
      source === 'vscode' ? VS_CODE_EXTENSION_LINK : CURSOR_EXTENSION_LINK;
    window.location.href = link;
    onClose();
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50'>
      <div className='bg-white rounded-xl border-2 border-[#1A1A1A] shadow-[8px_8px_0px_0px_rgba(26,26,26,1)] w-full max-w-md p-6 relative'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-500 hover:text-gray-700'
          aria-label='Close'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-6 w-6'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        {!success ? (
          <>
            <h3 className='text-xl font-bold mb-4 text-center'>
              Join Our Community
            </h3>
            <p className='text-sm text-gray-600 mb-6 text-center'>
              Enter your email to download Takeoff for{' '}
              {source === 'vscode' ? 'VS Code' : 'Cursor'} and stay updated with
              new features.
            </p>

            <form onSubmit={handleSubmit}>
              <div className='mb-4'>
                <input
                  type='email'
                  placeholder='Enter your email'
                  className='w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6B46C1]'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {error && <p className='text-red-500 text-xs mt-1'>{error}</p>}
              </div>

              <button
                type='submit'
                className='w-full btn-primary mb-2'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </>
        ) : (
          <div className='text-center'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-green-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h3 className='text-xl font-bold mb-2'>Thank You!</h3>
            <p className='text-sm text-gray-600 mb-6'>
              Your email has been submitted successfully.
            </p>
            <button onClick={handleDownloadNow} className='w-full btn-primary'>
              Download Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
